import { Suspense, useEffect, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Headphones } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { PowerDialerSidebar } from '../PowerDialer';
import { IncomingCallToast } from '../IncomingCallToast';
import { InCallModal } from '../InCallModal';
import { ConnectionBanner } from '../ConnectionBanner';
import { ViewAsBanner } from '../ViewAsSwitcher';
import { GuidedTour } from '../Tour';
import { UserMenu } from '../UserMenu';
import { MeldenKnopf } from '../Melden';
import { ErrorBoundary } from '../ErrorBoundary';
import { LoadingHint } from '../LoadingHint';
import { useAkteBeimAnruf } from '../../lib/akteBeimAnruf';
import { NAV_ITEMS, NAV_SECTIONS, type NavItem } from '../../lib/nav';
import { PRIVILEGED_ROLES } from '../../types';
import './Layout.css';

export function Layout() {
  const { hasRole } = useAuthStore();
  const apply = useThemeStore((s) => s.apply);
  const location = useLocation();
  const canSeeTeam = hasRole(...PRIVILEGED_ROLES);
  const canSeeAdmin = hasRole('admin');
  // Auswertungen auch für Studioleiter, nicht nur admin/supervisor.
  const canSeeInsights = hasRole(...PRIVILEGED_ROLES, 'studioleiter');

  useEffect(() => apply(), [apply]);

  // Beim Anruf öffnet sich die Kundenakte von selbst (abschaltbar in den
  // Einstellungen) — steht hier, weil es unabhängig von der offenen Seite gilt.
  useAkteBeimAnruf();

  const canSee: Record<NonNullable<NavItem['privileged']>, boolean> = {
    team: canSeeTeam, admin: canSeeAdmin, insights: canSeeInsights,
    // Skript-/Leitfaden-Texte: Admin + Supervisor (nicht nur Admin).
    scripts: canSeeTeam,
  };
  const visibleItems = NAV_ITEMS.filter((it) => !it.privileged || canSee[it.privileged]);

  // Navigation nach Abschnitten gruppiert, mit Überschriften.
  const sidebarNav = (): ReactNode =>
    NAV_SECTIONS.map((sec) => {
      const items = visibleItems.filter((it) => it.section === sec.id);
      if (items.length === 0) return null;
      return (
        <div key={sec.id} style={{ marginTop: sec.label ? 10 : 0 }}>
          {sec.label && <div className="cc-navsection">{sec.label}</div>}
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} end={it.end} className="cc-navlink" title={it.label}>
                <Icon size={16} />
                <span className="cc-navlink-text">{it.label}</span>
              </NavLink>
            );
          })}
        </div>
      );
    });

  return (
    <div className="cc-layout">
      <aside className="cc-sidebar">
        <div className="cc-sidebar-brand">
          <Headphones size={20} />
          <span>Randori-Pro Callcenter</span>
        </div>
        <UserMenu />
        {/* Feedback: fehlt etwas oder hakt es? Direkt als Ticket melden. */}
        <MeldenKnopf />
        <nav className="cc-sidebar-nav" data-tour="nav">{sidebarNav()}</nav>
      </aside>
      <main className="cc-main">
        {/* Gilt für alle Seiten: sagt Bescheid, wenn eine Abfrage klemmt, und
            nennt das Tastenkürzel zum Neuladen. */}
        <LoadingHint />
        {/* Fängt Fehler EINER Seite ab, ohne Navigation und laufendes Gespräch
            mitzureißen; springt beim Seitenwechsel automatisch zurück. */}
        <ErrorBoundary resetKey={location.pathname}>
          {/* Eigene Ladegrenze für den Inhaltsbereich: beim Seitenwechsel
              bleiben Navigation, Dialer und laufendes Gespräch stehen, statt
              dass kurz die ganze Oberfläche durch „Lade…" ersetzt wird. */}
          <Suspense fallback={<div style={{ padding: 24, color: 'var(--text-secondary)' }}>Lade…</div>}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
      <ConnectionBanner />
      <ViewAsBanner />
      <PowerDialerSidebar />
      <IncomingCallToast />
      <InCallModal />
      <GuidedTour />
    </div>
  );
}
